import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { envConfig } from '@src/utils/envConfig';
import { preventIPAccess, shouldForwardStyledProp } from '@talos/kyoko';
import { initAgGridAndCharts, initBigJs, initDatadog, initWhyDidYouRender } from '@talos/kyoko/src/init';
import { enableMapSet } from 'immer';
import { isNil } from 'lodash-es';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - react-sparklines does not have types, but noImplicitAny is still false, so this is necessary to always skip; TODO: change to @ts-expect-error when resolved
import { SparklinesText } from 'react-sparklines';
import { StyleSheetManager } from 'styled-components';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './utils/sugar';

preventIPAccess();

const environment = envConfig.VITE_AVA_ENV;
const revision = import.meta.env.VITE_GIT_HASH;
const version = import.meta.env.VITE_NPM_PACKAGE_VERSION;

initWhyDidYouRender(environment);
initDatadog({
  environment,
  version: ['prod', 'sandbox'].includes(environment) ? version : revision,
  applicationId: envConfig.VITE_DATADOG_APP_ID,
  clientToken: envConfig.VITE_DATADOG_CLIENT_TOKEN,
  proxy: envConfig.VITE_DATADOG_PROXY_URL,
  service: 'whitelabel-ui',
  startSessionReplayRecordingManually: true,
});
initAgGridAndCharts();
initBigJs();
enableMapSet();

const rootElement = document.getElementById('root');
if (isNil(rootElement)) {
  throw Error('Could not find root element to render app into');
}
const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <StyleSheetManager shouldForwardProp={shouldForwardStyledProp}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StyleSheetManager>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

if (envConfig.VITE_AVA_ENV === 'local') {
  // ok, since it's expected to be removed in production
  // eslint-disable-next-line react/forbid-foreign-prop-types
  delete (SparklinesText as any).propTypes.text;
}
